/**
 * Templates — hub page for every reusable blueprint in the platform.
 *
 * Sections rendered top-to-bottom:
 *   1. Proposal Templates — fully built. Reusable proposal blueprints
 *      that get cloned into a fresh draft from a client's Proposals tab.
 *   2. Email Templates (coming soon) — will surface SendGrid Dynamic
 *      Templates (`d-xxxxx`) so admins can pick one when scheduling a
 *      client marketing campaign.
 *   3. Report Templates (coming soon) — preset monthly-report layouts.
 *
 * The placeholder cards are intentionally visible so the navigation
 * tells the full product story even before the data is wired up.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, FileText, Loader2, Trash2, ExternalLink, Files, Mail, BarChart3,
  Construction, Edit3, Newspaper, Sparkles, Tag, Receipt, LayoutTemplate,
  Search, Check, Image as ImageIcon,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


function fmtMoney(v, cur = "AUD") {
  const sym = { AUD: "$", USD: "$", GBP: "£", EUR: "€", NZD: "$" }[cur] || "";
  return `${sym}${Math.round(Number(v || 0)).toLocaleString()}`;
}


export default function TemplatesListPage() {
  return (
    <div className="space-y-10" data-testid="templates-list-page">
      <div>
        <div className="label-overline">Library</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Templates</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
          Reusable blueprints — proposals today, emails and reports next.
          Build a template once and instantiate it per client whenever you
          need it.
        </p>
      </div>

      <ProposalTemplatesSection />
      <EmailTemplatesSection />
      <ReportTemplatesSection />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Proposal Templates — fully built section
 * ────────────────────────────────────────────────────────────────── */
function ProposalTemplatesSection() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/proposal-templates`);
      setRows(data?.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates");
      setRows([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/proposal-templates`, {});
      toast.success("Template created");
      navigate(`/templates/${data.template_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      await api.delete(`/proposal-templates/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="section-proposal-templates" className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-zinc-200 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#002FA7]/10">
            <FileText className="h-4 w-4 text-[#002FA7]" />
          </span>
          <div>
            <h2 className="font-display font-bold text-xl">Proposal Templates</h2>
            <p className="text-[12px] text-zinc-500">
              Cloned into a fresh draft from the client&apos;s Proposals tab.
            </p>
          </div>
        </div>
        <Button onClick={createNew} disabled={creating}
          className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
          data-testid="new-template-btn">
          {creating
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Plus className="h-4 w-4 mr-2" />}
          New proposal template
        </Button>
      </div>

      {rows === null ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent></Card>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-10 text-center space-y-3">
            <Files className="h-10 w-10 text-zinc-300 mx-auto" />
            <div className="font-display font-bold text-xl">No templates yet</div>
            <p className="text-sm text-zinc-500 max-w-lg mx-auto">
              Click <strong>New proposal template</strong> to start a blank
              blueprint, or open any existing proposal and press{" "}
              <strong>Copy to templates</strong> to save its structure for reuse.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="templates-list">
          {rows.map((t) => (
            <Card key={t.template_id} className="rounded-sm hover:border-zinc-400 transition-colors">
              <CardContent className="p-4 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-6 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button"
                      onClick={() => navigate(`/templates/${t.template_id}`)}
                      className="font-display font-bold text-base hover:underline truncate text-left"
                      data-testid={`template-title-${t.template_id}`}>
                      {t.title || "Untitled template"}
                    </button>
                    <Badge className="rounded-sm font-mono text-[10px] bg-purple-100 text-purple-800 border-purple-200">
                      TEMPLATE
                    </Badge>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate">
                    Updated{" "}
                    {t.updated_at
                      ? new Date(t.updated_at).toLocaleString("en-AU", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })
                      : "—"}
                    {" · "}{(t.sections || []).length} section{(t.sections || []).length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="col-span-6 md:col-span-3 text-right md:text-left">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">One-off · Monthly</div>
                  <div className="font-mono text-sm">
                    {fmtMoney(t.totals?.one_off_total, t.currency)} ·{" "}
                    <span className="text-zinc-600">{fmtMoney(t.totals?.monthly_total, t.currency)}</span>
                  </div>
                </div>

                <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-1.5">
                  <a target="_blank" rel="noreferrer"
                    href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/proposal-templates/${t.template_id}/pdf`}
                    className="text-xs underline text-zinc-600"
                    data-testid={`template-pdf-link-${t.template_id}`}>
                    <ExternalLink className="h-3.5 w-3.5 inline" /> PDF
                  </a>
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => navigate(`/templates/${t.template_id}`)}
                    data-testid={`template-edit-${t.template_id}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
                    onClick={() => remove(t.template_id)}
                    data-testid={`template-delete-${t.template_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Email Templates — live SendGrid Dynamic Template list.
 * ────────────────────────────────────────────────────────────────── */
function EmailTemplatesSection() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [showLegacy, setShowLegacy] = useState(false);
  // editing shape: null | { mode: "new", starter?: starterObj } | { mode: "edit", template: tpl }
  const [editing, setEditing] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const refresh = useCallback(async () => {
    setErr(null);
    setRows(null);
    try {
      const { data } = await api.get(
        `/sendgrid/templates?generations=${showLegacy ? "both" : "dynamic"}`);
      setRows(data?.templates || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load SendGrid templates");
      setRows([]);
    }
  }, [showLegacy]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Removes it permanently from SendGrid.`)) return;
    try {
      await api.delete(`/sendgrid/templates/${t.id}`);
      toast.success("Template deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="section-email-templates" className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-zinc-200 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#1A82E2]/10">
            <Mail className="h-4 w-4 text-[#1A82E2]" />
          </span>
          <div>
            <h2 className="font-display font-bold text-xl">Email Templates</h2>
            <p className="text-[12px] text-zinc-500">
              SendGrid Dynamic Templates — create + edit them right here, or
              jump to <a href="https://mc.sendgrid.com/dynamic-templates" target="_blank"
                rel="noreferrer" className="underline">SendGrid&apos;s visual designer
                <ExternalLink className="h-3 w-3 inline ml-0.5" /></a>{" "}
              for richer layouts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-zinc-600 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showLegacy}
              onChange={(e) => setShowLegacy(e.target.checked)}
              data-testid="show-legacy-toggle" />
            Include legacy
          </label>
          <Button size="sm" variant="outline" onClick={refresh}
            className="rounded-sm" data-testid="email-templates-refresh">
            <Loader2 className={`h-3.5 w-3.5 mr-1.5 ${rows === null ? "animate-spin" : "hidden"}`} />
            Refresh
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => setGalleryOpen(true)}
            className="rounded-sm border-[#1A82E2]/40 text-[#1A82E2] hover:bg-[#1A82E2]/5"
            data-testid="open-sendgrid-gallery-btn">
            <LayoutTemplate className="h-3.5 w-3.5 mr-1.5" />
            SendGrid Design Gallery
          </Button>
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}
            className="rounded-sm bg-[#1A82E2] hover:bg-[#1573CC] text-white"
            data-testid="new-email-template-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Blank template
          </Button>
        </div>
      </div>

      {/* Starter Gallery — one-click HTML scaffolds. */}
      <div className="space-y-2" data-testid="email-starter-gallery">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-[#1A82E2]" />
          <span className="label-overline">Start from a template</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {EMAIL_STARTERS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setEditing({ mode: "new", starter: s })}
                data-testid={`email-starter-${s.key}`}
                className="text-left rounded-sm border border-zinc-200 bg-white p-3
                  hover:border-zinc-400 hover:shadow-sm transition-all group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-7 w-7 rounded-sm grid place-items-center"
                    style={{ background: `${s.accent}15` }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: s.accent }} />
                  </span>
                  <span className="font-display font-bold text-sm">{s.label}</span>
                </div>
                <p className="text-[11px] text-zinc-500 leading-snug">{s.blurb}</p>
                <div className="mt-2 text-[10px] font-mono text-[#1A82E2]
                  opacity-0 group-hover:opacity-100 transition-opacity">
                  Use this starter →
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <Card className="rounded-sm">
          <CardContent className="p-4 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm">
            <strong>Couldn&apos;t reach SendGrid:</strong> {err}
          </CardContent>
        </Card>
      )}

      {rows === null && !err ? (
        <Card className="rounded-sm">
          <CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates from SendGrid…
          </CardContent>
        </Card>
      ) : (rows || []).length === 0 && !err ? (
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center space-y-2">
            <Mail className="h-10 w-10 text-zinc-300 mx-auto" />
            <div className="font-display font-bold text-lg">
              No Dynamic Templates yet.
            </div>
            <p className="text-[12px] text-zinc-500 max-w-lg mx-auto">
              Pick a starter above for a one-click responsive scaffold, or
              click <strong>Blank template</strong> to write your own HTML.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {rows.map((t) => (
            <Card key={t.id} className="rounded-sm hover:border-zinc-400 transition-colors">
              <CardContent className="p-3 grid grid-cols-12 gap-3 items-center">
                <div className="col-span-12 md:col-span-7 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm truncate">
                      {t.name || "(unnamed)"}
                    </span>
                    <Badge className={`rounded-sm font-mono text-[9px] ${
                      t.generation === "dynamic"
                        ? "bg-[#1A82E2]/10 text-[#1A82E2] border-[#1A82E2]/20"
                        : "bg-zinc-100 text-zinc-700 border-zinc-200"
                    }`}>
                      {t.generation || "?"}
                    </Badge>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
                    {t.id}
                    {t.subject && <> · {t.subject}</>}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3 text-[11px] font-mono text-zinc-600">
                  {t.version_count} ver{t.version_count === 1 ? "" : "s"}
                  {t.updated_at && (
                    <> · {String(t.updated_at).slice(0, 10)}</>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-1.5">
                  {t.generation === "dynamic" && (
                    <Button size="sm" variant="outline" className="rounded-sm h-7"
                      onClick={() => setEditing({ mode: "edit", template: t })}
                      data-testid={`email-template-edit-${t.id}`}>
                      <Edit3 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  )}
                  <a target="_blank" rel="noreferrer"
                    href={`https://mc.sendgrid.com/dynamic-templates/${t.id}`}
                    className="text-xs underline text-zinc-600 inline-flex items-center gap-0.5">
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                  {t.generation === "dynamic" && (
                    <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600"
                      onClick={() => remove(t)}
                      data-testid={`email-template-delete-${t.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <EmailTemplateEditor
          existing={editing.mode === "edit" ? editing.template : null}
          starter={editing.mode === "new" ? (editing.starter || null) : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {galleryOpen && (
        <SendGridDesignGalleryModal
          onClose={() => setGalleryOpen(false)}
          onImportedTemplate={() => { setGalleryOpen(false); refresh(); }}
        />
      )}

      <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
        Send a campaign with one of these templates from any client&apos;s{" "}
        <span className="font-mono">SendGrid</span> tab — the &quot;Send
        campaign&quot; button there will pick a template, accept a recipient
        list, and fire <span className="font-mono">POST /v3/mail/send</span>{" "}
        with per-recipient personalisation.
      </p>
    </section>
  );
}

function EmailTemplateEditor({ existing, starter, onClose, onSaved }) {
  const isNew = !existing;
  const [name, setName] = useState(
    existing?.name || starter?.suggested_name || ""
  );
  const [subject, setSubject] = useState(
    existing?.subject || starter?.suggested_subject || ""
  );
  const [html, setHtml] = useState(starter?.html || "");
  const [activeStarter, setActiveStarter] = useState(starter?.key || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Lazy-fetch HTML of the active version when editing an existing template.
  useEffect(() => {
    if (isNew) return;  // starter or blank — html already set
    let cancelled = false;
    api.get(`/sendgrid/templates/${existing.id}`)
      .then((r) => {
        if (cancelled) return;
        const versions = r.data?.versions || [];
        const active = versions.find((v) => v.active === 1) || versions[0];
        setSubject((s) => s || active?.subject || "");
        setHtml(active?.html_content || "");
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't load template");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [existing, isNew]);

  // Swap starter — re-seed the editor only if the user hasn't typed anything
  // meaningful yet, otherwise confirm before overwriting.
  const pickStarter = (s) => {
    if (html && html !== starter?.html && !window.confirm(
      "Replace the current HTML with this starter? Your edits will be lost."
    )) return;
    setActiveStarter(s.key);
    setHtml(s.html);
    if (!name || name === starter?.suggested_name) setName(s.suggested_name);
    if (!subject || subject === starter?.suggested_subject) setSubject(s.suggested_subject);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/sendgrid/templates", {
          name: name.trim(),
          subject: subject.trim(),
          html_content: html,
        });
        toast.success("Template created");
      } else {
        await api.patch(`/sendgrid/templates/${existing.id}`, {
          name: name.trim(),
          subject: subject.trim(),
          html_content: html,
        });
        toast.success("Template updated");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight">
            {isNew
              ? (starter ? `New email — ${starter.label}` : "New email template")
              : `Edit · ${existing.name}`}
          </DialogTitle>
          <DialogDescription>
            Use{" "}
            <code className="font-mono text-[11px] bg-zinc-100 px-1 rounded-sm">
              &#123;&#123;first_name&#125;&#125;
            </code>{" "}
            placeholders anywhere. They&apos;ll resolve to{" "}
            <code className="font-mono text-[11px] bg-zinc-100 px-1 rounded-sm">
              dynamic_template_data
            </code>{" "}
            at send time.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {isNew && (
              <div className="flex items-center gap-1.5 flex-wrap pb-1"
                data-testid="starter-switcher">
                <span className="label-overline mr-1">Starter:</span>
                {EMAIL_STARTERS.map((s) => {
                  const Icon = s.icon;
                  const active = activeStarter === s.key;
                  return (
                    <button key={s.key} type="button" onClick={() => pickStarter(s)}
                      data-testid={`starter-switch-${s.key}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm
                        text-[11px] border transition-colors ${active
                          ? "bg-[#1A82E2] text-white border-[#1A82E2]"
                          : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                        }`}>
                      <Icon className="h-3 w-3" /> {s.label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 rounded-sm h-9 text-xs"
                  placeholder="e.g. Monthly newsletter — Spring 2026"
                  data-testid="email-template-name" />
              </div>
              <div>
                <Label className="label-overline">Default subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="mt-1 rounded-sm h-9 text-xs"
                  placeholder="Hi {{first_name}}, your monthly update is here"
                  data-testid="email-template-subject" />
              </div>
            </div>

            <div>
              <Label className="label-overline flex items-center justify-between">
                <span>HTML body</span>
                <button type="button" onClick={() => setShowPreview((p) => !p)}
                  className="text-[10px] font-mono text-[#1A82E2] hover:underline normal-case tracking-normal">
                  {showPreview ? "Edit HTML" : "Preview rendered"}
                </button>
              </Label>
              {showPreview ? (
                <div className="mt-1 border border-zinc-200 rounded-sm bg-white"
                  style={{ maxHeight: "60vh", overflow: "auto" }}>
                  <iframe srcDoc={html} title="Template preview"
                    sandbox=""
                    className="w-full"
                    style={{ minHeight: "300px", height: "60vh", border: 0 }}
                    data-testid="email-template-preview" />
                </div>
              ) : (
                <textarea value={html} onChange={(e) => setHtml(e.target.value)}
                  className="mt-1 w-full rounded-sm border border-zinc-200 bg-white p-2 text-xs font-mono resize-y"
                  style={{ minHeight: "240px", maxHeight: "60vh" }}
                  spellCheck={false}
                  data-testid="email-template-html" />
              )}
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                Recommended size: ≤ 100 KB. Inline CSS — Outlook ignores
                <code className="bg-zinc-100 px-1 rounded-sm ml-0.5">&lt;style&gt;</code> blocks.
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onClose}
            disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading}
            className="rounded-sm bg-[#1A82E2] hover:bg-[#1573CC] text-white"
            data-testid="email-template-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {isNew ? "Create template" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * SendGrid Design Gallery — 60 pre-built email designs Twilio
 * publishes via `GET /v3/designs/pre-builts`. Picking one opens a
 * full-size preview pane with two import options:
 *   1. "Use as Dynamic Template" — creates a fresh `d-xxxxx` template
 *      with the design's HTML body so it appears in the Email
 *      Templates list and is immediately usable for /v3/mail/send.
 *   2. "Save to SendGrid Design Library" — server-side duplicate
 *      that drops the design into Twilio's own "Your Email Designs"
 *      tab for visual editing inside SendGrid itself.
 * ────────────────────────────────────────────────────────────────── */
function SendGridDesignGalleryModal({ onClose, onImportedTemplate }) {
  const [data, setData] = useState(null);   // { designs, categories, total }
  const [err, setErr] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);  // selected design row
  const [detail, setDetail] = useState(null);  // hydrated GET-one payload

  useEffect(() => {
    let cancelled = false;
    setErr(null); setData(null);
    api.get("/sendgrid/pre-built-designs")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.response?.data?.detail || "Failed to load gallery");
      });
    return () => { cancelled = true; };
  }, []);

  // Lazy-fetch the full HTML body whenever a design is picked.
  useEffect(() => {
    if (!picked) { setDetail(null); return; }
    let cancelled = false;
    setDetail(null);
    api.get(`/sendgrid/pre-built-designs/${picked.id}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e.response?.data?.detail || "Couldn't load preview");
        setPicked(null);
      });
    return () => { cancelled = true; };
  }, [picked]);

  const filteredDesigns = useMemo(() => {
    let list = data?.designs || [];
    if (activeCategory) {
      list = list.filter((d) => (d.categories || []).some(
        (c) => c.toLowerCase() === activeCategory.toLowerCase()));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((d) =>
        (d.name || "").toLowerCase().includes(q) ||
        (d.subject || "").toLowerCase().includes(q));
    }
    return list;
  }, [data, activeCategory, search]);

  const thumbSrc = (url) => {
    if (!url) return null;
    return url.startsWith("//") ? `https:${url}` : url;
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="sendgrid-gallery-modal">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-200">
          <DialogTitle className="font-display font-bold tracking-tight
            flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-[#1A82E2]" />
            SendGrid Design Gallery
            {data && (
              <Badge className="rounded-sm font-mono text-[10px] bg-[#1A82E2]/10
                text-[#1A82E2] border-[#1A82E2]/20">
                {data.total} designs
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Pre-built responsive email designs maintained by Twilio SendGrid.
            Pick one and import it as a Dynamic Template (ready to send) or
            save it into SendGrid&apos;s Design Library for visual editing.
          </DialogDescription>
        </DialogHeader>

        {err ? (
          <div className="p-6 text-[12px] text-amber-900 bg-amber-50
            border border-amber-200 rounded-sm m-5">
            <strong>Couldn&apos;t reach SendGrid:</strong> {err}
          </div>
        ) : !data ? (
          <div className="p-12 flex items-center justify-center gap-2
            text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the design gallery…
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-12 gap-0">
            {/* Sidebar — categories */}
            <aside className="col-span-3 border-r border-zinc-200 overflow-y-auto
              bg-zinc-50/40" data-testid="sendgrid-gallery-categories">
              <div className="p-3 sticky top-0 bg-zinc-50/95 backdrop-blur
                border-b border-zinc-200">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 text-zinc-400 absolute
                    left-2 top-1/2 -translate-y-1/2" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search designs…"
                    className="w-full rounded-sm border border-zinc-200 bg-white
                      pl-7 pr-2 py-1.5 text-[11px] focus:outline-none
                      focus:border-zinc-400"
                    data-testid="sendgrid-gallery-search" />
                </div>
              </div>
              <div className="p-2">
                <button type="button"
                  onClick={() => setActiveCategory(null)}
                  data-testid="gallery-cat-all"
                  className={`w-full text-left px-2 py-1.5 rounded-sm text-[12px]
                    transition-colors ${activeCategory === null
                      ? "bg-[#1A82E2] text-white font-semibold"
                      : "text-zinc-700 hover:bg-white"}`}>
                  All designs
                  <span className="float-right text-[10px] font-mono
                    opacity-70">{data.total}</span>
                </button>
                {(data.categories || []).map((c) => (
                  <button key={c.name} type="button"
                    onClick={() => setActiveCategory(c.name)}
                    data-testid={`gallery-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full text-left px-2 py-1.5 rounded-sm text-[12px]
                      transition-colors ${activeCategory === c.name
                        ? "bg-[#1A82E2] text-white font-semibold"
                        : "text-zinc-700 hover:bg-white"}`}>
                    {c.name}
                    <span className="float-right text-[10px] font-mono
                      opacity-70">{c.count}</span>
                  </button>
                ))}
              </div>
            </aside>

            {/* Center — thumbnail grid */}
            <div className="col-span-5 overflow-y-auto p-4 bg-white"
              data-testid="sendgrid-gallery-grid">
              {filteredDesigns.length === 0 ? (
                <div className="text-center text-[12px] text-zinc-500 py-12">
                  No designs match this filter.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredDesigns.map((d) => {
                    const isPicked = picked?.id === d.id;
                    return (
                      <button key={d.id} type="button"
                        onClick={() => setPicked(d)}
                        data-testid={`gallery-design-${d.id}`}
                        className={`group text-left rounded-sm border bg-white
                          overflow-hidden transition-all ${isPicked
                            ? "border-[#1A82E2] ring-2 ring-[#1A82E2]/30"
                            : "border-zinc-200 hover:border-zinc-400 hover:shadow-sm"}`}>
                        <div className="aspect-[3/4] bg-zinc-100 overflow-hidden
                          relative">
                          {thumbSrc(d.thumbnail_url) ? (
                            <img
                              src={thumbSrc(d.thumbnail_url)}
                              alt={d.name}
                              loading="lazy"
                              className="w-full h-full object-cover object-top
                                group-hover:scale-[1.02] transition-transform" />
                          ) : (
                            <div className="w-full h-full grid place-items-center
                              text-zinc-300">
                              <ImageIcon className="h-8 w-8" />
                            </div>
                          )}
                          {isPicked && (
                            <div className="absolute top-1.5 right-1.5
                              h-5 w-5 rounded-full bg-[#1A82E2] text-white
                              grid place-items-center">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <div className="font-display font-semibold text-[12px]
                            leading-tight truncate">
                            {d.name}
                          </div>
                          {(d.categories || []).length > 0 && (
                            <div className="text-[10px] font-mono text-zinc-500
                              truncate mt-0.5">
                              {d.categories.join(" · ")}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right pane — preview + import */}
            <div className="col-span-4 border-l border-zinc-200 overflow-y-auto
              bg-zinc-50/40" data-testid="sendgrid-gallery-preview">
              {!picked ? (
                <div className="h-full grid place-items-center p-6 text-center">
                  <div className="text-[12px] text-zinc-500 max-w-[220px]
                    leading-relaxed">
                    <LayoutTemplate className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
                    Pick a design to preview it and choose an import path.
                  </div>
                </div>
              ) : !detail ? (
                <div className="p-6 flex items-center justify-center gap-2
                  text-zinc-500 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
                </div>
              ) : (
                <PreviewAndImportPane
                  design={detail}
                  onImportedTemplate={onImportedTemplate} />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewAndImportPane({ design, onImportedTemplate }) {
  const [name, setName] = useState(design.name || "");
  const [subject, setSubject] = useState(design.subject || "");
  const [importing, setImporting] = useState(null);  // "design" | "dynamic_template" | null

  const importDesign = async (mode) => {
    if (mode === "dynamic_template" && !name.trim()) {
      toast.error("Template name is required"); return;
    }
    setImporting(mode);
    try {
      const { data } = await api.post(
        `/sendgrid/pre-built-designs/${design.id}/import`,
        { mode, name: name.trim() || undefined,
          subject: subject.trim() || undefined });
      if (mode === "dynamic_template") {
        toast.success(`Imported as Dynamic Template "${data.name}"`);
        onImportedTemplate?.();
      } else {
        toast.success(`Saved to SendGrid Design Library as "${data.name}"`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="p-3 space-y-3">
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <iframe srcDoc={design.html_content} title={design.name}
          sandbox=""
          className="w-full bg-white"
          style={{ minHeight: "320px", height: "320px", border: 0 }}
          data-testid="sendgrid-gallery-iframe-preview" />
      </div>

      <div className="space-y-2">
        <div>
          <Label className="label-overline">Template name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1 rounded-sm h-8 text-[12px]"
            placeholder="e.g. Monthly newsletter — Spring 2026"
            data-testid="gallery-import-name" />
        </div>
        <div>
          <Label className="label-overline">Default subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
            className="mt-1 rounded-sm h-8 text-[12px]"
            placeholder="Subject line shown in inbox"
            data-testid="gallery-import-subject" />
        </div>
      </div>

      <div className="space-y-2 pt-1">
        <Button
          onClick={() => importDesign("dynamic_template")}
          disabled={!!importing}
          className="w-full rounded-sm bg-[#1A82E2] hover:bg-[#1573CC] text-white
            justify-start"
          data-testid="import-as-dynamic-template-btn">
          {importing === "dynamic_template"
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
            : <Mail className="h-3.5 w-3.5 mr-2" />}
          Use as Dynamic Template
        </Button>
        <p className="text-[10px] text-zinc-500 leading-relaxed -mt-1 ml-1">
          Creates a fresh <code className="bg-zinc-100 px-1 rounded-sm
          font-mono">d-xxxxx</code> template and lands it in the Email
          Templates list below — ready for campaign sends.
        </p>

        <Button
          variant="outline"
          onClick={() => importDesign("design")}
          disabled={!!importing}
          className="w-full rounded-sm justify-start"
          data-testid="import-as-design-btn">
          {importing === "design"
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
            : <Files className="h-3.5 w-3.5 mr-2" />}
          Save to SendGrid Design Library
        </Button>
        <p className="text-[10px] text-zinc-500 leading-relaxed -mt-1 ml-1">
          Drops the design into SendGrid&apos;s &ldquo;Your Email Designs&rdquo;
          tab so you can edit it inside SendGrid&apos;s visual builder, then
          convert to a Dynamic Template from there.
        </p>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Starter Gallery — 4 responsive HTML scaffolds users can clone with
 * one click. Each template uses Handlebars placeholders so SendGrid's
 * `dynamic_template_data` payload can hydrate them at send time.
 * ────────────────────────────────────────────────────────────────── */
const EMAIL_STARTERS = [
  {
    key: "newsletter",
    label: "Monthly Newsletter",
    blurb: "Hero, headline, two-column article grid + footer.",
    accent: "#002FA7",
    icon: Newspaper,
    suggested_name: "Monthly Newsletter",
    suggested_subject: "Hi {{first_name}}, your {{month}} update is here",
    html: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,Helvetica,sans-serif;color:#18181b;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr><td align="center" style="padding:24px;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0"
        style="background:#ffffff;border:1px solid #e4e4e7;border-radius:4px;">
        <tr><td style="background:#002FA7;padding:28px 32px;color:#ffffff;">
          <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;opacity:.7;">
            {{month}} Edition
          </div>
          <h1 style="margin:6px 0 0 0;font-size:26px;font-weight:800;">
            {{company}} Monthly
          </h1>
        </td></tr>
        <tr><td style="padding:28px 32px 12px 32px;">
          <p style="margin:0 0 12px 0;font-size:15px;line-height:1.6;">
            Hi {{first_name}}, here&apos;s what&apos;s new this month.
          </p>
          <p style="margin:0;font-size:14px;line-height:1.6;color:#52525b;">
            Lead paragraph — set the tone, summarise the issue and tee up the
            articles below.
          </p>
        </td></tr>
        <tr><td style="padding:8px 32px 24px 32px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
            <tr>
              <td width="50%" style="padding:8px 8px 8px 0;vertical-align:top;">
                <div style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#71717a;">
                  Article 1
                </div>
                <h3 style="margin:4px 0 6px 0;font-size:16px;">{{article_1_title}}</h3>
                <p style="margin:0 0 8px 0;font-size:13px;line-height:1.55;color:#52525b;">
                  {{article_1_excerpt}}
                </p>
                <a href="{{article_1_url}}" style="font-size:12px;color:#002FA7;text-decoration:underline;">
                  Read more →
                </a>
              </td>
              <td width="50%" style="padding:8px 0 8px 8px;vertical-align:top;">
                <div style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#71717a;">
                  Article 2
                </div>
                <h3 style="margin:4px 0 6px 0;font-size:16px;">{{article_2_title}}</h3>
                <p style="margin:0 0 8px 0;font-size:13px;line-height:1.55;color:#52525b;">
                  {{article_2_excerpt}}
                </p>
                <a href="{{article_2_url}}" style="font-size:12px;color:#002FA7;text-decoration:underline;">
                  Read more →
                </a>
              </td>
            </tr>
          </table>
        </td></tr>
      </table>
      <p style="font-size:11px;color:#a1a1aa;margin-top:16px;">
        Sent by {{company}} · <a href="{{unsubscribe}}" style="color:#a1a1aa;">unsubscribe</a>
      </p>
    </td></tr>
  </table>
</body>
</html>`,
  },
  {
    key: "welcome",
    label: "Welcome Onboarding",
    blurb: "Friendly intro + 3 next-step checklist + CTA.",
    accent: "#0EA371",
    icon: Sparkles,
    suggested_name: "Welcome — New Client Onboarding",
    suggested_subject: "Welcome to {{company}}, {{first_name}} 👋",
    html: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,Helvetica,sans-serif;color:#18181b;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr><td align="center" style="padding:24px;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0"
        style="background:#ffffff;border:1px solid #e4e4e7;border-radius:4px;">
        <tr><td style="padding:32px 32px 8px 32px;text-align:center;">
          <div style="display:inline-block;background:#0EA371;color:#ffffff;
            width:48px;height:48px;line-height:48px;border-radius:24px;
            font-size:22px;font-weight:700;">
            👋
          </div>
          <h1 style="margin:14px 0 6px 0;font-size:24px;font-weight:800;">
            Welcome aboard, {{first_name}}!
          </h1>
          <p style="margin:0;font-size:14px;line-height:1.6;color:#52525b;">
            We&apos;re thrilled to have {{company}} on board. Here&apos;s how to
            hit the ground running.
          </p>
        </td></tr>
        <tr><td style="padding:24px 32px 8px 32px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
            <tr><td style="padding:8px 0;border-bottom:1px solid #f4f4f5;">
              <strong style="color:#0EA371;font-size:13px;">Step 1.</strong>
              <span style="font-size:14px;color:#27272a;">Confirm your kick-off call slot.</span>
            </td></tr>
            <tr><td style="padding:8px 0;border-bottom:1px solid #f4f4f5;">
              <strong style="color:#0EA371;font-size:13px;">Step 2.</strong>
              <span style="font-size:14px;color:#27272a;">Share access to your ad accounts.</span>
            </td></tr>
            <tr><td style="padding:8px 0;">
              <strong style="color:#0EA371;font-size:13px;">Step 3.</strong>
              <span style="font-size:14px;color:#27272a;">Tell us your goals for the first 90 days.</span>
            </td></tr>
          </table>
        </td></tr>
        <tr><td style="padding:16px 32px 32px 32px;text-align:center;">
          <a href="{{portal_url}}"
            style="display:inline-block;background:#0EA371;color:#ffffff;
            text-decoration:none;padding:12px 22px;border-radius:4px;
            font-size:14px;font-weight:600;">
            Open your portal
          </a>
        </td></tr>
      </table>
      <p style="font-size:11px;color:#a1a1aa;margin-top:16px;">
        Need a hand? Reply to this email — we&apos;re here.<br/>
        Sent by {{company}} · <a href="{{unsubscribe}}" style="color:#a1a1aa;">unsubscribe</a>
      </p>
    </td></tr>
  </table>
</body>
</html>`,
  },
  {
    key: "promo",
    label: "Promotion / Offer",
    blurb: "Bold hero offer, discount code chip and dual CTA.",
    accent: "#E11D48",
    icon: Tag,
    suggested_name: "Promo — Limited-time offer",
    suggested_subject: "{{first_name}}, save {{discount}} this week only",
    html: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /></head>
<body style="margin:0;padding:0;background:#fff1f2;font-family:Arial,Helvetica,sans-serif;color:#18181b;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr><td align="center" style="padding:24px;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0"
        style="background:#ffffff;border:1px solid #fecdd3;border-radius:4px;overflow:hidden;">
        <tr><td style="background:#E11D48;padding:36px 32px 28px 32px;text-align:center;color:#ffffff;">
          <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;opacity:.85;">
            Limited Time
          </div>
          <h1 style="margin:8px 0 4px 0;font-size:36px;font-weight:800;line-height:1.1;">
            Save {{discount}}
          </h1>
          <div style="font-size:15px;opacity:.9;">on {{product_name}}</div>
        </td></tr>
        <tr><td style="padding:28px 32px 8px 32px;text-align:center;">
          <p style="margin:0;font-size:15px;line-height:1.6;">
            Hi {{first_name}}, just for you — use the code below at checkout.
          </p>
          <div style="margin:18px auto 6px auto;display:inline-block;
            border:2px dashed #E11D48;padding:10px 22px;border-radius:4px;
            font-family:'Courier New',monospace;font-size:18px;
            font-weight:700;letter-spacing:3px;color:#E11D48;">
            {{promo_code}}
          </div>
          <div style="font-size:12px;color:#71717a;">Expires {{expiry_date}}</div>
        </td></tr>
        <tr><td style="padding:24px 32px 32px 32px;text-align:center;">
          <a href="{{shop_url}}"
            style="display:inline-block;background:#E11D48;color:#ffffff;
            text-decoration:none;padding:12px 22px;border-radius:4px;
            font-size:14px;font-weight:600;margin-right:8px;">
            Shop now
          </a>
          <a href="{{learn_more_url}}"
            style="display:inline-block;background:#ffffff;color:#E11D48;
            text-decoration:none;padding:12px 22px;border-radius:4px;
            font-size:14px;font-weight:600;border:1px solid #E11D48;">
            Learn more
          </a>
        </td></tr>
      </table>
      <p style="font-size:11px;color:#a1a1aa;margin-top:16px;">
        Sent by {{company}} · <a href="{{unsubscribe}}" style="color:#a1a1aa;">unsubscribe</a>
      </p>
    </td></tr>
  </table>
</body>
</html>`,
  },
  {
    key: "transactional",
    label: "Transactional Receipt",
    blurb: "Clean receipt layout with line items and total.",
    accent: "#18181b",
    icon: Receipt,
    suggested_name: "Receipt / Invoice confirmation",
    suggested_subject: "Receipt #{{invoice_number}} from {{company}}",
    html: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /></head>
<body style="margin:0;padding:0;background:#fafafa;font-family:Arial,Helvetica,sans-serif;color:#18181b;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
    <tr><td align="center" style="padding:24px;">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0"
        style="background:#ffffff;border:1px solid #e4e4e7;border-radius:4px;">
        <tr><td style="padding:28px 32px 8px 32px;border-bottom:1px solid #f4f4f5;">
          <table role="presentation" width="100%"><tr>
            <td style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#71717a;">
              Receipt
            </td>
            <td align="right" style="font-family:'Courier New',monospace;font-size:12px;color:#71717a;">
              #{{invoice_number}}
            </td>
          </tr></table>
          <h1 style="margin:8px 0 0 0;font-size:22px;font-weight:800;">
            Thanks, {{first_name}}.
          </h1>
          <p style="margin:6px 0 0 0;font-size:13px;color:#52525b;">
            We&apos;ve received your payment on {{paid_date}}.
          </p>
        </td></tr>
        <tr><td style="padding:20px 32px 8px 32px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"
            style="font-size:13px;">
            {{#each line_items}}
            <tr><td style="padding:8px 0;border-bottom:1px solid #f4f4f5;">
              {{this.description}}
            </td>
            <td align="right" style="padding:8px 0;border-bottom:1px solid #f4f4f5;
              font-family:'Courier New',monospace;">
              {{this.amount}}
            </td></tr>
            {{/each}}
            <tr><td style="padding:14px 0 0 0;font-weight:700;">Total</td>
            <td align="right" style="padding:14px 0 0 0;font-weight:700;
              font-family:'Courier New',monospace;font-size:16px;">
              {{total}}
            </td></tr>
          </table>
        </td></tr>
        <tr><td style="padding:16px 32px 28px 32px;">
          <a href="{{invoice_url}}"
            style="display:inline-block;background:#18181b;color:#ffffff;
            text-decoration:none;padding:10px 18px;border-radius:4px;font-size:13px;">
            View invoice
          </a>
        </td></tr>
      </table>
      <p style="font-size:11px;color:#a1a1aa;margin-top:16px;">
        Questions? Reply to this email.<br/>
        Sent by {{company}} · <a href="{{unsubscribe}}" style="color:#a1a1aa;">unsubscribe</a>
      </p>
    </td></tr>
  </table>
</body>
</html>`,
  },
];

/* ──────────────────────────────────────────────────────────────────
 * Report Templates — placeholder for future preset monthly reports.
 * ────────────────────────────────────────────────────────────────── */
function ReportTemplatesSection() {
  return (
    <section data-testid="section-report-templates" className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-zinc-200 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-emerald-100">
            <BarChart3 className="h-4 w-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display font-bold text-xl">Report Templates</h2>
            <p className="text-[12px] text-zinc-500">
              Preset layouts for monthly PDF reports — different platforms,
              ordering and tone of voice per industry vertical.
            </p>
          </div>
        </div>
      </div>
      <Card className="rounded-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Construction className="h-4 w-4 text-amber-600" />
            Coming soon
          </div>
          <p className="text-[12px] text-zinc-600 leading-relaxed max-w-3xl mt-1">
            Will let admins pick a default report template per client (or per
            client tier) so the monthly PDF picks the right cover page, section
            order and copy register automatically.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
